import $ from "jquery";
import FileService from "./FileService";
import {
  cleanUpGimmicks,
  generateGimmicks,
  activateDescriptions,
  setRequiredAttribute,
  removeRequiredAttribute,
} from "./ServiceFunctions";

class MachineryService {
  /**
   * contain machinery html objects
   *
   * @property $machinery
   * @type {Object}
   */
  $machinery = {};

  /**
   * html identifier
   *
   * @property controlType
   * @type {String}
   */
  controlType = ".js-machinery-control-type";

  /**
   * html identifier
   *
   * @property softwareType
   * @type {String}
   */
  softwareType = ".js-machinery-software";

  /**
   * html identifier
   *
   * @property manualTextField
   * @type {String}
   */
  manualTextField = ".js-manually-text";

  /**
   * html identifier
   *
   * @property gimmickContainer
   * @type {Object}
   */
  gimmickContainer = {
    documentation: ".js-documentation",
    software: ".js-software-option",
  };

  /**
   * html identifier
   *
   * @property gimmickSection
   * @type {String}
   */
  gimmickSection = ".js-gimmicks";

  gimmickExcludeLanguages = ["it-IT"];

  /**
   * instance of registration service
   *
   * @property fileService
   * @type {FileService}
   */
  fileService;

  /**
   *  registration button
   *
   * @property $addRegistrationSection
   * @type {Object}
   */
  $addRegistrationSection = $(".js-add-registration");

  /**
   * registration selector
   *
   * @property typeOfRegistrationSelector
   * @type {String}
   */
  typeOfRegistrationSelector = ".js-type-of-registration";

  /**
   * prefix for localstorage
   *
   * @property machineLocalStoragePrefix
   * @type {String}
   */
  machineLocalStoragePrefix = "machineRegistration_";

  /**
   * contains defauld header of first option of select
   * @type {string}
   */
  softwareHeader = '<option value="">Software</option>';

  machineryControllerJson = [];

  machineAvailability = false;

  constructor(machinery, machineAvailability) {
    this.fileService = new FileService();
    this.$machinery = machinery;
    this.machineAvailability = machineAvailability;
    this.setSoftwareHeader();
    this.initializeMachine();

    const jsonElement = $("[data-machinery-controller-json]");
    if (jsonElement.length) {
      let machineryControllerJson = jsonElement.data(
        "machinery-controller-json"
      );
      if (typeof machineryControllerJson === "string") {
        machineryControllerJson = JSON.parse(machineryControllerJson);
      }
      this.machineryControllerJson = machineryControllerJson;
    }
  }

  /**
   * initialize all machineries
   * @method  initializeMachine
   */
  initializeMachine() {
    $.each(this.$machinery, (i, machine) => {
      this.enableControlTypeSelector($(machine), i);
      this.enableKindOfRegistrationSelector($(machine));
      activateDescriptions($(machine));
      this.fileService.initFileService($(machine));
    });
  }

  /**
   * @method setSoftwareHeader
   */
  setSoftwareHeader() {
    if ($(this.softwareType).children()[0] !== "undefined") {
      this.softwareHeader = $(this.softwareType).children()[0];
    }
  }

  /**
   * enable control type select
   *
   * @method enableControlTypeSelector
   * @param $machine
   * @param index identify position to know if its first or second or ...
   */
  enableControlTypeSelector($machine, index) {
    // activate control type selector
    $machine.find(this.controlType).on("change", () => {
      // clean gimmicks
      cleanUpGimmicks($machine, this.gimmickContainer["documentation"]);

      // remove possibility software values on change
      $machine.find(this.softwareType).empty();

      // delete local storage for this machine (key)
      this.removeRecentSelected(index);

      // handle new machine selection
      this.handleMachineController($machine, index);
    });
  }

  /**
   * handle new selected machine
   * @param $machine
   * @param index identify position to know if its first or second or ...
   */
  handleMachineController($machine, index) {
    const controller = $machine.find(this.controlType).val();
    const labelIdentifier = "-controller";

    if (
      controller.length &&
      Object.prototype.hasOwnProperty.call(
        this.machineryControllerJson,
        controller
      )
    ) {
      // set software for current controller
      if (
        Object.prototype.hasOwnProperty.call(
          this.machineryControllerJson[controller],
          "software"
        )
      ) {
        if (this.machineryControllerJson[controller].software) {
          $machine.find(this.softwareType).append($(this.softwareHeader));
          Object.keys(
            this.machineryControllerJson[controller].software
          ).forEach((software) => {
            $machine.find(this.softwareType).append(
              $("<option>", {
                value: software,
                text: software,
              })
            );
          });

          // add event on change to set into local storage machine[0]['software']
          $machine.find(this.softwareType).on("change", (e) => {
            this.setSelectedSoftware($(e.currentTarget).val(), index);
          });

          // when last request failed try to restore selected
          this.handleSelectedSoftware($machine.find(this.softwareType), index);
        }
      }

      // set Gimmicks for current controller
      if (
        Object.prototype.hasOwnProperty.call(
          this.machineryControllerJson[controller],
          "documentation"
        )
      ) {
        if (this.machineryControllerJson[controller].documentation.length > 0) {
          generateGimmicks(
            this.machineryControllerJson[controller].documentation,
            this.gimmickContainer["documentation"],
            $machine,
            labelIdentifier
          );
        }
      }

      // remove select header from control type
      this.visibilityAndAccessHandler($machine);
      this.enableSoftwareSelector($machine);

      // add event on change to set into local storage machine[0]['gimmick']
      $machine.find(".js-gimmick").on("change", (e) => {
        this.setSelectedGimmick($(e.currentTarget).val(), index);
      });

      // when last request failed try to restore selected
      this.handleSelectedGimmick($machine.find(".js-gimmick"), index);

      this.updateVisibility($machine);
    } else {
      cleanUpGimmicks($machine, this.gimmickContainer["software"]);
    }
  }

  /**
   * enable software select
   *
   * @method enableSoftwareSelector
   * @param $machine
   */
  enableSoftwareSelector($machine) {
    const controller = $machine.find(this.controlType).val();
    this.setGimmicksBySoftware(controller, $machine);
    this.updateVisibility($machine);
    $machine.find(this.softwareType).on("change", () => {
      const machineValue = $machine.find(this.controlType).val();
      this.setGimmicksBySoftware(machineValue, $machine);
      this.updateVisibility($machine);
    });
  }

  /**
   * set current gimmicks
   *
   * @method setGimmicksBySoftware
   * @param controller
   * @param $machine
   */
  setGimmicksBySoftware(controller, $machine) {
    const labelIdentifier = "-software";
    cleanUpGimmicks($machine, this.gimmickContainer["software"]);

    // set Gimmicks for first software
    const software = $machine.find(this.softwareType).val();

    // if any software option is available
    if (
      Object.prototype.hasOwnProperty.call(
        this.machineryControllerJson[controller].software,
        software
      )
    ) {
      if (this.machineryControllerJson[controller].software[software].length) {
        generateGimmicks(
          // available software options for current selected software
          this.machineryControllerJson[controller]["software"][software],
          // container HTML identifier
          this.gimmickContainer["software"],
          // current machine DOM
          $machine,
          // unique label for radio label
          labelIdentifier
        );
      }
    }
    this.updateVisibility($machine);
  }

  /**
   * access and visibility handler for selects
   *
   * @method visibilityAndAccessHandler
   * @param machine
   */
  visibilityAndAccessHandler(machine) {
    // enable software types
    machine.find(this.softwareType).removeAttr("disabled");
    if (machine.find(this.gimmickSection).length) {
      // set visibility on gimmicks
      machine.find(this.gimmickSection).show();
    } else {
      $(this.$addRegistrationSection).hide();
    }

    // set visibility on registration button
    if (this.machineAvailability) {
      $(this.$addRegistrationSection).show();
    }
  }

  /**
   * if no gimmick available, hide selector
   *
   * @param machine
   */
  updateVisibility(machine) {
    const availableDocumentation = machine
      .find(this.gimmickSection)
      .find(".js-documentation option").length;
    const availableSoftware = machine
      .find(this.gimmickSection)
      .find(".js-software-option option").length;

    if (
      (availableDocumentation === 0 && availableSoftware === 0) ||
      this.isCurrentLanguageExcludedForVisibility()
    ) {
      machine.find(this.gimmickSection).hide();
    }
  }

  /**
   * check if current language
   * @returns {boolean}
   */
  isCurrentLanguageExcludedForVisibility() {
    const language = $("html").attr("lang");
    return this.gimmickExcludeLanguages.includes(language);
  }

  /**
   * enable control type select
   *
   * @method enableKindOfRegistrationSelector
   * @param $machine
   */
  enableKindOfRegistrationSelector($machine) {
    $machine.find(this.typeOfRegistrationSelector).on("change", (e) => {
      const kindOfRegistration = $(e.currentTarget).val();

      if (kindOfRegistration === "automatically") {
        setRequiredAttribute(
          $($machine.find(".js-automatically-registration")),
          "input"
        );
        removeRequiredAttribute(
          $($machine.find(".js-manually-registration")),
          "input"
        );
        $machine.find(".js-automatically-registration").show();
        $machine.find(".js-manually-registration").hide();
        $(e.currentTarget).find("option[value='']").remove();
      }

      if (kindOfRegistration === "manually") {
        setRequiredAttribute(
          $($machine.find(".js-manually-registration")),
          "input"
        );
        removeRequiredAttribute(
          $($machine.find(".js-automatically-registration")),
          "input"
        );
        $machine.find(".js-manually-registration").show();
        $machine.find(".js-automatically-registration").hide();
        $(e.currentTarget).find("option[value='']").remove();
      }
    });
  }

  /**
   * clean or remove all data for that machine position
   * @param index identify position to know if its first or second or ...
   */
  removeRecentSelected(index) {
    localStorage.removeItem(this.machineLocalStoragePrefix + index);
  }

  /**
   * get all stored data for that machine position
   * @param index identify position to know if its first or second or ...
   * @return Object
   */
  getRecentSelected(index) {
    const data = JSON.parse(
      localStorage.getItem(this.machineLocalStoragePrefix + index)
    );
    if (data === null) {
      return {};
    }
    return data;
  }

  /**
   * set selected software for given machine index
   * @param software name of software as string
   * @param index identify position to know if its first or second or ...
   */
  setSelectedSoftware(software, index) {
    const data = this.getRecentSelected(index);
    data["software"] = software;
    localStorage.setItem(
      this.machineLocalStoragePrefix + index,
      JSON.stringify(data)
    );
  }

  /**
   * set recent selected software to machine software select element when available
   * @param softwareSelectElement select element of software
   * @param index identify position to know if its first or second or ...
   */
  handleSelectedSoftware(softwareSelectElement, index) {
    const data = this.getRecentSelected(index);
    if (data && data["software"]) {
      $(softwareSelectElement).val(data["software"]);
    }
  }

  /**
   * set selected gimmick for given machine index
   * @param gimmick name of gimmick as string
   * @param index identify position to know if its first or second or ...
   */
  setSelectedGimmick(gimmick, index) {
    const data = this.getRecentSelected(index);
    data["gimmick"] = gimmick;
    localStorage.setItem(
      this.machineLocalStoragePrefix + index,
      JSON.stringify(data)
    );
  }

  /**
   * set recent selected gimmick to machine gimmick select element when available
   * @param gimmickSelectElement select element of gimmick
   * @param index identify position to know if its first or second or ...
   */
  handleSelectedGimmick(gimmickSelectElement, index) {
    const data = this.getRecentSelected(index);
    if (data && data["gimmick"]) {
      $(gimmickSelectElement).val(data["gimmick"]);
    }
  }
}

export default MachineryService;
